import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, ShieldCheck, Users, Star } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBwcm9mZXNzaW9uYWxzJTIwb2ZmaWNlJTIwbWVldGluZ3xlbnwwfHx8fDE3ODI3NTQwMTh8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative isolate overflow-hidden pt-32 lg:pt-40 pb-24 lg:pb-32 bg-slate-900">
      {/* Background image with overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="Corporate workforce collaborating in a modern office"
          loading="eager"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-900/75 to-primary/40" />
        <div className="absolute inset-0 bg-grid-hero opacity-40" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs sm:text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-white/90 font-medium tracking-wide">Trusted by 100+ corporates across India</span>
            </div>

            <h1 className="mt-6 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Empowering Businesses with{" "}
              <span className="bg-gradient-to-r from-white via-blue-100 to-blue-300 bg-clip-text text-transparent">
                Smart HR & Workforce Solutions
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed">
              Advance HR Technologies provides end-to-end recruitment, staffing, payroll management
              and HR consultancy services across India — helping businesses build high-performing
              teams efficiently and compliantly.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-slate-900 font-semibold text-sm shadow-sm hover:bg-blue-50 transition-colors"
              >
                Get Free Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-proposal"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-primary text-white font-semibold text-sm shadow-sm hover:bg-primary-hover transition-colors border border-white/20"
              >
                Request Proposal
              </Link>
              <a
                href="tel:+917977261570"
                data-testid="hero-cta-call"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white/0 text-white font-semibold text-sm border border-white/25 hover:bg-white/10 transition-colors backdrop-blur-sm"
              >
                <PhoneCall className="w-4 h-4" />
                +91 79772 61570
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { v: "1000+", l: "Candidates Placed" },
                { v: "100+", l: "Corporate Clients" },
                { v: "95%", l: "Client Satisfaction" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-white/20 pl-4">
                  <div className="font-heading font-extrabold text-2xl sm:text-3xl text-white">{s.v}</div>
                  <div className="text-xs sm:text-sm text-white/65 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating glass cards */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="absolute -top-6 -left-6 w-72 rounded-2xl bg-white/95 backdrop-blur-md shadow-2xl shadow-black/20 p-5 border border-white/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-heading font-bold text-slate-900 text-sm">Compliance Driven</div>
                  <div className="text-xs text-slate-500">PF • ESIC • PT • Labour Law</div>
                </div>
              </div>
            </div>
            <div className="ml-auto mt-32 w-80 rounded-2xl bg-white/95 backdrop-blur-md shadow-2xl shadow-black/20 p-6 border border-white/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-heading font-bold text-slate-900 text-sm">Fast Hiring TAT</div>
                  <div className="text-xs text-slate-500">7–15 days average</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-1 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="mt-2 text-xs text-slate-600 leading-relaxed">
                  "Strategic hiring partner for our blue-collar mandates across 4 plants."
                </p>
                <p className="mt-2 text-[11px] text-slate-500">— Rajeev M., Head of HR</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
